import { Controller } from "@hotwired/stimulus";

// Drives the "Lancer l'édition" / "Document généré" lifecycle inside a doc
// detail modal. Clicking the action button POSTs to the generated_documents
// endpoint and then polls /status every 3s until the worker finishes.
//
// Visual states (toggled via the [data-state] attribute on the root):
//   idle       → "Lancer l'édition" button
//   processing → spinner + "Génération en cours…"
//   done       → "Modifier le document" link to google_link
//   failed     → "Réessayer" + error chip
export default class extends Controller {
  static targets = ["statusLabel", "actionsRoot"];
  static values = {
    documentClass: String,
    documentId:    Number,
    createUrl:     String,
    statusUrl:     String,
    state:         { type: String, default: "idle" },
    googleLink:    String,
    generatedUrl:  String,
  };

  connect() {
    if (this.stateValue === "processing") this._startPolling();
  }

  disconnect() {
    this._stopPolling();
  }

  generate(event) {
    event?.preventDefault();
    if (this.stateValue === "processing") return;

    this._setState("processing");
    fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      },
      credentials: "same-origin",
      body: JSON.stringify({
        generated_document: {
          document_class: this.documentClassValue,
          document_id: this.documentIdValue,
        },
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.generation_status === "started") {
          this._startPolling();
        } else {
          // Erreur actionnable renvoyée par le serveur (ex. aucun modèle
          // rattaché) : affichée sous le bouton Réessayer.
          this._lastError = data.error || null;
          this._setState("failed");
        }
      })
      .catch(() => this._setState("failed"));
  }

  _startPolling() {
    this._stopPolling();
    this._pollTimer = setInterval(() => this._poll(), 3000);
  }

  _stopPolling() {
    if (this._pollTimer) clearInterval(this._pollTimer);
    this._pollTimer = null;
  }

  _poll() {
    fetch(`${this.statusUrlValue}?klass=${this.documentClassValue}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.generation_status === "done") {
          this.googleLinkValue = data.google_link || "";
          this._setState("done");
          this._stopPolling();
        } else if (data.generation_status === "failed") {
          this._setState("failed");
          this._stopPolling();
        }
        // "processing" stays — keep polling
      })
      .catch(() => {
        this._setState("failed");
        this._stopPolling();
      });
  }

  _setState(state) {
    this.stateValue = state;
    this.element.dataset.state = state;
    this.actionsRootTarget.innerHTML = this._renderActions(state);
    if (this.hasStatusLabelTarget) {
      this.statusLabelTarget.textContent = this._stateLabel(state);
      this.statusLabelTarget.className = `esm_doc-status esm_doc-status--${state}`;
    }
  }

  _stateLabel(state) {
    return {
      idle:       "À générer",
      processing: "Génération en cours…",
      done:       "Document généré",
      failed:     "Échec de la génération",
    }[state] || state;
  }

  _renderActions(state) {
    if (state === "processing") {
      return `
        <button class="esm_btn esm_btn--ghost" disabled>
          <i class="fa-solid fa-spinner fa-spin"></i> Génération en cours…
        </button>
      `;
    }
    if (state === "done") {
      const link = this.googleLinkValue || this.generatedUrlValue;
      if (link) {
        return `
          <a href="${link}" target="_blank" rel="noopener" class="esm_btn esm_btn--success">
            <i class="fa-solid fa-up-right-from-square"></i> Modifier le document
          </a>
        `;
      }
      return `
        <button class="esm_btn esm_btn--ghost" disabled>
          <i class="fa-solid fa-circle-check"></i> Document généré (lien indisponible)
        </button>
      `;
    }
    if (state === "failed") {
      // Message serveur (string statique côté Rails, pas de contenu user).
      const note = this._lastError
        ? `<p class="esm_doc-error-note text-danger small mt-2 mb-0">${this._lastError}</p>`
        : "";
      return `
        <button class="esm_btn esm_btn--primary" data-action="click->document-generator#generate">
          <i class="fa-solid fa-rotate-right"></i> Réessayer
        </button>
        ${note}
      `;
    }
    // idle
    return `
      <button class="esm_btn esm_btn--primary" data-action="click->document-generator#generate">
        <i class="fa-solid fa-wand-magic-sparkles"></i> Lancer l'édition
      </button>
    `;
  }
}
